import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, ApiError, type VercelAdminSandbox } from "@/lib/api";
import { relativeIso } from "@/lib/format";
import { SandboxesPanel } from "@/pages/admin/SandboxesPanel";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Clock, RefreshCw, Square, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const EXTEND_MS = 15 * 60 * 1000;

const STATUS_DOT: Partial<Record<VercelAdminSandbox["status"], string>> = {
  running: "bg-green-500",
  pending: "bg-sky-500",
  snapshotting: "bg-sky-500",
  stopping: "bg-amber-500",
  stopped: "bg-muted-foreground/50",
  failed: "bg-destructive",
  aborted: "bg-destructive",
};

function VercelSandboxActions({ containerId, sb }: { containerId: string; sb: VercelAdminSandbox }) {
  const queryClient = useQueryClient();
  const opts = (verb: string) => ({
    onSuccess: () => {
      toast.success(`Sandbox ${verb}`);
      queryClient.invalidateQueries({ queryKey: ["admin-vercel-sandboxes", containerId] });
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : `Sandbox ${verb} failed`),
  });

  const stop = useMutation({ mutationFn: () => api.admin.stopVercelSandbox(containerId, sb.name), ...opts("stopped") });
  const remove = useMutation({ mutationFn: () => api.admin.deleteVercelSandbox(containerId, sb.name), ...opts("deleted") });
  const extend = useMutation({
    mutationFn: () => api.admin.extendVercelSandbox(containerId, sb.name, EXTEND_MS),
    ...opts("extended by 15m"),
  });
  const busy = stop.isPending || remove.isPending || extend.isPending;
  const running = sb.status === "running";

  return (
    <div className="flex items-center justify-end gap-1.5">
      <Button
        variant="outline"
        size="sm"
        disabled={busy || !running}
        onClick={() => extend.mutate()}
        title={running ? "Add 15 minutes to the session timeout" : "Only available for running sandboxes"}
      >
        <Clock className="size-3 mr-1" />
        Extend 15m
      </Button>
      {sb.persistent ? (
        <Button
          variant="outline"
          size="sm"
          disabled={busy || !(running || sb.status === "pending")}
          onClick={() => stop.mutate()}
          title="Stop the session; its filesystem is snapshotted and Boo resumes it by name"
        >
          <Square className="size-3 mr-1" />
          Stop
        </Button>
      ) : (
        // An ephemeral sandbox keeps nothing once stopped, so Stop is as final as Delete.
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" disabled={busy || !(running || sb.status === "pending")}>
              <Square className="size-3 mr-1" />
              Stop
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Stop this sandbox?</AlertDialogTitle>
              <AlertDialogDescription>
                Sandbox <span className="font-mono">{sb.name}</span> is ephemeral: stopping it ends the run
                and its files are gone. <strong>This cannot be undone.</strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => stop.mutate()}>Stop sandbox</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" size="sm" disabled={busy}>
            <Trash2 className="size-3 mr-1" />
            Delete
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this sandbox?</AlertDialogTitle>
            <AlertDialogDescription>
              Deletes sandbox <span className="font-mono">{sb.name}</span> and its snapshots. Its files are
              gone; Boo starts a fresh sandbox for this thread on the next turn.{" "}
              <strong>This cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate()}>Delete sandbox</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// The Sandboxes tab for a brain on Vercel Sandbox: every sandbox tagged with
// this container, newest first, all statuses in one list (Vercel can't filter
// by status). No pause/resume on Vercel — a persistent sandbox stops at its
// timeout and resumes by name.
export function VercelSandboxesPanel({ containerId }: { containerId: string }) {
  const queryClient = useQueryClient();
  const [threadFilter, setThreadFilter] = useState("");
  const [showE2b, setShowE2b] = useState(false);

  const query = useInfiniteQuery({
    queryKey: ["admin-vercel-sandboxes", containerId],
    queryFn: ({ pageParam }) => api.admin.vercelSandboxesPage(containerId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    retry: (count, err) => !(err instanceof ApiError && err.status === 409) && count < 2,
  });

  const loaded = query.data?.pages.flatMap((p) => p.sandboxes) ?? [];
  const thread = threadFilter.trim().toLowerCase();
  const rows = thread ? loaded.filter((s) => (s.threadId ?? "").toLowerCase().includes(thread)) : loaded;
  const errorCode = query.error instanceof ApiError ? query.error.code : null;
  // No Vercel credentials reachable for this Boo: a setup gap, not a failure.
  const noAccessMessage = errorCode === "vercel_access_missing" ? query.error?.message : null;
  // The cached record said vercel but the backend says e2b: refetch the record
  // so the tab switches to the E2B panel.
  const staleRecord = errorCode === "sandboxes_on_e2b";
  useEffect(() => {
    if (staleRecord) queryClient.invalidateQueries({ queryKey: ["admin-container", containerId] });
  }, [staleRecord, queryClient, containerId]);
  const access = query.data?.pages[0]?.access;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium">Vercel sandboxes</span>
        <Input
          placeholder="Filter by thread"
          value={threadFilter}
          onChange={(e) => setThreadFilter(e.target.value)}
          className="h-9 w-64"
        />
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto gap-1"
          disabled={query.isFetching}
          onClick={() => query.refetch()}
        >
          <RefreshCw className={`size-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {query.isLoading ? (
        <LoadingIndicator />
      ) : noAccessMessage ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-400">
          {noAccessMessage}
        </div>
      ) : query.isError ? (
        <ErrorComponent error={query.error ?? "Failed to load sandboxes"} />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2">
          {loaded.length === 0
            ? "No Vercel sandboxes yet."
            : query.hasNextPage
              ? "No sandboxes match the filter on the loaded pages — load more to keep searching."
              : "No sandboxes match the filter."}
        </p>
      ) : (
        <div className="rounded-lg border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Sandbox</th>
                <th className="px-4 py-2 font-medium">Provider / thread</th>
                <th className="px-4 py-2 font-medium">Lifetime</th>
                <th className="px-4 py-2 font-medium">Resources</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((sb) => (
                <tr key={sb.name} className="align-top">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      <span className={`inline-block size-2 rounded-full ${STATUS_DOT[sb.status] ?? "bg-muted"}`} />
                      {sb.status}
                    </span>
                    <div className="text-xs text-muted-foreground">{sb.persistent ? "persistent" : "ephemeral"}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs break-all">{sb.name}</td>
                  <td className="px-4 py-3 text-xs">
                    <div>{sb.provider ?? "—"}</div>
                    <div className="text-muted-foreground break-all">{sb.threadId ?? "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                    <div>Created {relativeIso(sb.createdAt)}</div>
                    {sb.expiresAt && <div>Expires {relativeIso(sb.expiresAt)}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    <div>
                      {sb.vcpus ?? "?"} vCPU{sb.memoryMB !== null ? ` · ${sb.memoryMB} MB` : ""}
                    </div>
                    <div className="text-muted-foreground">{sb.region ?? "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <VercelSandboxActions containerId={containerId} sb={sb} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {!noAccessMessage && (
          <span>
            {thread ? `${rows.length} shown · ${loaded.length} loaded` : `${loaded.length} loaded`}
          </span>
        )}
        {query.hasNextPage && (
          <Button
            variant="outline"
            size="sm"
            disabled={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            {query.isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        )}
        {!query.hasNextPage && loaded.length > 0 && <span>End of list.</span>}
        {access && (
          <span>
            Listed with {access === "team-token" ? "the team token" : "the brain's own Vercel credentials"}.
          </span>
        )}
        <Button variant="link" size="sm" className="ml-auto h-auto p-0 text-xs" onClick={() => setShowE2b((v) => !v)}>
          {showE2b ? "Hide E2B sandboxes" : "Show E2B sandboxes kept for rollback"}
        </Button>
      </div>

      {showE2b && (
        <div className="space-y-2 border-t pt-4">
          <p className="text-xs text-muted-foreground">
            E2B sandboxes from before the move. Moving back to E2B reconnects them with their files.
          </p>
          <SandboxesPanel containerId={containerId} initialState="paused" />
        </div>
      )}
    </div>
  );
}
